// lib/vue2-uniapp-converter.ts

/**
 * 将HTML+CSS转换为Vue 2风格的uni-app代码
 */
export function convertToVue2Uniapp(htmlWithCss: string): string {
  try {
    console.log('开始转换HTML+CSS到uni-app');
    
    // 提取HTML和CSS
    const html = extractHtmlFromCode(htmlWithCss);
    const css = extractCssFromCode(htmlWithCss);
    
    // 转换HTML标签
    let convertedHtml = html
      .replace(/<div\b([^>]*)>/gi, '<view$1>')
      .replace(/<\/div>/gi, '</view>')
      .replace(/<span\b([^>]*)>/gi, '<text$1>')
      .replace(/<\/span>/gi, '</text>')
      .replace(/<img\b([^>]*)>/gi, '<image$1 mode="widthFix">')
      .replace(/<a\b([^>]*)>/gi, '<view$1>')
      .replace(/<\/a>/gi, '</view>')
      .replace(/<button\b([^>]*)>/gi, '<button$1>')
      .replace(/<input\b([^>]*)>/gi, '<input$1>')
      .replace(/<form\b([^>]*)>/gi, '<form$1>')
      .replace(/<label\b([^>]*)>/gi, '<label$1>')
      .replace(/<select\b([^>]*)>/gi, '<picker$1>')
      .replace(/<option\b([^>]*)>/gi, '<picker-item$1>');
    
    // 转换事件
    convertedHtml = convertedHtml
      .replace(/\bonclick=/gi, '@tap=')
      .replace(/\bonchange=/gi, '@change=')
      .replace(/\boninput=/gi, '@input=')
      .replace(/\bonsubmit=/gi, '@submit=')
      .replace(/\bonfocus=/gi, '@focus=')
      .replace(/\bonblur=/gi, '@blur=');
    
    // 转换CSS为uni-app兼容格式
    const convertedCss = convertCssForUniapp(css);
    
    // 提取数据和方法
    const data = extractDataFromHtml(html);
    const methods = extractMethodsFromHtml(html);
    
    // 构建uni-app页面
    // 修改 convertToVue2Uniapp 函数的返回部分：
return `<template>
  <view class="page-container">
    ${convertedHtml}
  </view>
</template>

<script>
export default {
  data() {
    return {
      ${data}
    }
  },
  methods: {
    ${methods}
    
   
  },
  onLoad() {
    console.log('页面加载');
  },
  onShow() {
    console.log('页面显示');
  }
}
</script>

<style lang="scss" scoped>
// SCSS变量定义
$primary-color: #2c5aa0;
$primary-light: #4a7bc1;
$secondary-color: #d4af37;
$background-color: #f8f9fa;
$text-color: #333333;
$text-light: #666666;
$border-color: #e0e0e0;
$white: #ffffff;
$shadow: 0 8rpx 24rpx rgba(0, 0, 0, 0.08);
$border-radius: 24rpx;
$transition: all 0.3s ease;

.page-container {
  min-height: 100vh;
  background-color: $background-color;
  box-sizing: border-box;
}

${convertedCss}
</style>`;
    
  } catch (error) {
    console.error('转换到uni-app失败:', error);
    throw new Error(`uni-app转换失败: ${error instanceof Error ? error.message : '未知错误'}`);
  }
}

/**
 * 提取HTML中的数据
 */
function extractDataFromHtml(html: string): string {
  const data: string[] = [];
  
  // 提取输入框数据
  const inputRegex = /<input[^>]*name=["']([^"']+)["'][^>]*>/gi;
  let match;
  
  while ((match = inputRegex.exec(html)) !== null) {
    const nameMatch = html.substring(match.index).match(/name=["']([^"']+)["']/);
    if (nameMatch && nameMatch[1]) {
      const name = nameMatch[1].replace(/-/g, '_');
      data.push(`${name}: ''`);
    }
  }
  
  // 提取复选框/单选框状态
  if (html.includes('type="checkbox"') || html.includes('type="radio"')) {
    data.push('checkedItems: []');
  }
  
  // 如果有表单
  if (html.includes('<form') || html.includes('@submit') || html.includes('onsubmit')) {
    data.push('formData: {}');
  }
  
  // 默认数据
  if (data.length === 0) {
    data.push('message: \'Hello uni-app\'', 'count: 0');
  }
  
  return data.join(',\n      ');
}

/**
 * 提取HTML中的方法
 */
function extractMethodsFromHtml(html: string): string {
  const methods: string[] = [];
  
  // 表单提交方法
  if (html.includes('<form') || html.includes('@submit') || html.includes('onsubmit')) {
    methods.push(`handleSubmit(e) {
      if (e) e.preventDefault();
      uni.showLoading({ title: '提交中...' });
      
      // 模拟提交
      setTimeout(() => {
        uni.hideLoading();
        uni.showToast({
          title: '提交成功',
          icon: 'success'
        });
      }, 1500);
    }`);
  }
  
  // 点击处理方法
  if (html.includes('@click') || html.includes('@tap') || html.includes('onclick')) {
    methods.push(`handleTap(item) {
      console.log('点击事件', item);
      this.count++;
      uni.showToast({
        title: \`点击了\${this.count}次\`,
        icon: 'none'
      });
    }`);
  }
  
  // 输入处理方法
  if (html.includes('<input') || html.includes('@input') || html.includes('oninput')) {
    methods.push(`handleInput(e, field) {
      if (field && e && e.detail && e.detail.value !== undefined) {
        this[field] = e.detail.value;
      }
    }`);
  }
  
  // uni-app通用方法
  methods.push(
    `showToast(title = '操作成功', icon = 'none') {
      uni.showToast({ title, icon });
    }`,
    `navigateTo(url) {
      uni.navigateTo({ url });
    }`,
    `goBack() {
      uni.navigateBack();
    }`
  );
  
  return methods.join(',\n    ');
}

/**
 * 转换CSS为uni-app兼容格式
 */
// 在 convertCssForUniapp 函数中：
function convertCssForUniapp(css: string): string {
  if (!css) return '';
  
  let converted = css;
  
  // 1. 将CSS变量(:root)转换为SCSS变量
  const cssVarRegex = /:root\s*\{([^}]+)\}/g;
  const cssVars: Array<{name: string, value: string}> = [];
  
  let match;
  while ((match = cssVarRegex.exec(css)) !== null) {
    const varContent = match[1];
    const varLines = varContent.split(';');
    
    varLines.forEach(line => {
      const trimmed = line.trim();
      if (trimmed.startsWith('--')) {
        const [name, ...valueParts] = trimmed.split(':');
        if (name && valueParts.length > 0) {
          const varName = name.trim().substring(2); // 去掉--
          const varValue = valueParts.join(':').trim();
          cssVars.push({name: varName, value: varValue});
        }
      }
    });
  }
  
  // 2. 移除:root定义
  converted = converted.replace(cssVarRegex, '');
  
  // 3. 将px转换为rpx
  converted = converted.replace(/(\d+)px/g, (match, p1) => {
    const pxValue = parseInt(p1, 10);
    // 750设计稿标准：1px = 2rpx
    return `${pxValue * 2}rpx`;
  });
  
  // 4. 替换CSS变量引用
  cssVars.forEach(({name, value}) => {
    const varRegex = new RegExp(`var\\(--${name}\\)`, 'g');
    converted = converted.replace(varRegex, value);
  });
  
  // 5. 处理伪类和uni-app不支持的选择器
  converted = converted.replace(/:hover/g, '.hover-class');
  converted = converted.replace(/::before/g, '/* before伪类在uni-app中不支持 */');
  converted = converted.replace(/::after/g, '/* after伪类在uni-app中不支持 */');
  
  // 6. 处理媒体查询（uni-app支持rpx单位的媒体查询）
  converted = converted.replace(/@media\s*\(max-width:\s*(\d+)px\)/g, (match, p1) => {
    const pxValue = parseInt(p1, 10);
    return `@media (max-width: ${pxValue * 2}rpx)`;
  });
  
  // 7. 添加SCSS变量定义（如果有CSS变量）
  let scssVars = '';
  if (cssVars.length > 0) {
    scssVars = '// SCSS变量定义\n';
    cssVars.forEach(({name, value}) => {
      scssVars += `$${name}: ${value};\n`;
    });
    scssVars += '\n';
  }
  
  return scssVars + converted;
}


// 在 vue2-uniapp-converter.ts 中添加更智能的数据提取
function extractFormData(html: string): { data: string[], formFields: Array<{name: string, type: string}> } {
  const data: string[] = [];
  const formFields: Array<{name: string, type: string}> = [];
  
  // 提取所有input元素
  const inputRegex = /<input[^>]*>/gi;
  let match;
  
  while ((match = inputRegex.exec(html)) !== null) {
    const inputTag = match[0];
    
    // 获取name或id
    const nameMatch = inputTag.match(/name=["']([^"']+)["']/) || 
                     inputTag.match(/id=["']([^"']+)["']/) ||
                     inputTag.match(/placeholder=["']([^"']+)["']/);
    
    let fieldName = 'inputValue';
    if (nameMatch && nameMatch[1]) {
      // 将中文转换为英文，如"请输入手机号码" -> "phone"
      const chinese = nameMatch[1];
      if (chinese.includes('手机') || chinese.includes('电话')) {
        fieldName = 'phone';
      } else if (chinese.includes('密码')) {
        fieldName = 'password';
      } else if (chinese.includes('验证码')) {
        fieldName = 'verifyCode';
      } else {
        // 生成通用字段名
        fieldName = chinese.replace(/[^a-zA-Z0-9]/g, '').toLowerCase() || 'inputValue';
      }
    }
    
    // 获取type
    const typeMatch = inputTag.match(/type=["']([^"']+)["']/);
    const fieldType = typeMatch ? typeMatch[1] : 'text';
    
    // 根据类型设置默认值
    let defaultValue = "''";
    if (fieldType === 'checkbox' || fieldType === 'radio') {
      defaultValue = 'false';
    } else if (fieldType === 'number') {
      defaultValue = '0';
    }
    
    data.push(`${fieldName}: ${defaultValue}`);
    formFields.push({ name: fieldName, type: fieldType });
  }
  
  // 如果有按钮，添加loading状态
  if (html.includes('button') || html.includes('@click') || html.includes('@tap')) {
    data.push('isLoading: false');
  }
  
  // 默认数据
  if (data.length === 0) {
    data.push('message: \'Hello uni-app\'', 'count: 0');
  }
  
  return { data, formFields };
}
/**
 * 提取HTML代码（去除CSS和JS）
 */
function extractHtmlFromCode(fullCode: string): string {
  let html = fullCode;
  
  // 移除<style>标签
  html = html.replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, '');
  
  // 移除<script>标签
  html = html.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '');
  
  // 移除注释
  html = html.replace(/<!--[\s\S]*?-->/g, '');
  
  return html.trim();
}

/**
 * 提取CSS代码
 */
function extractCssFromCode(fullCode: string): string {
  const styleMatches = fullCode.match(/<style[\s\S]*?>([\s\S]*?)<\/style>/i);
  if (styleMatches && styleMatches[1]) {
    return styleMatches[1].trim();
  }
  return '';
}